import React, { memo, ReactElement, useState } from 'react';
import { Box, Button, SxProps, Theme } from '@mui/material';
import { useInterval } from 'react-use';
import {
  MessageType,
  GlobalMessage,
  useResendVerificationEmailMutation,
  TResponseQueryError,
} from '@alycecom/services';
import { useDispatch, useSelector } from 'react-redux';

import { getGiftHashId } from '../../../store/data/data.selectors';
import { getMaxVerificationAttemptReached } from '../../../store/statuses/statuses.selectors';
import { updateStatuses } from '../../../store/statuses/statuses.actions';
import useTrackRecipient from '../../../../../hooks/useTrackRecipient';

export const styles: { [className: string]: SxProps<Theme> } = {
  root: {
    mt: 2,
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  button: {
    color: 'link.main',
  },
} as const;

const MAX_RESEND_DELAY_IN_SECONDS = 60;

const ResendLink = (): ReactElement => {
  const dispatch = useDispatch();
  const trackEvent = useTrackRecipient();

  const hashId = useSelector(getGiftHashId);
  const isAttemptReached = useSelector(getMaxVerificationAttemptReached);

  const [resendDelay, setResendDelay] = useState(isAttemptReached ? MAX_RESEND_DELAY_IN_SECONDS : 0);

  const [resendVerificationEmail, { isLoading }] = useResendVerificationEmailMutation();

  const { showGlobalMessage } = GlobalMessage.useGlobalMessage();

  const isDelayActive = resendDelay < MAX_RESEND_DELAY_IN_SECONDS;
  const delayPostfix = isDelayActive ? ` in ${MAX_RESEND_DELAY_IN_SECONDS - resendDelay}sec` : '';

  const disabled = isDelayActive || isLoading || isAttemptReached;

  useInterval(
    () => {
      setResendDelay(resendDelay + 1);
    },
    isDelayActive ? 1000 : null,
  );

  const handleResend = () => {
    resendVerificationEmail(hashId)
      .unwrap()
      .then(() => {
        trackEvent('Email verification — retried');
        setResendDelay(0);
        showGlobalMessage({ text: 'The verification code was resent.', type: MessageType.Success });
      })
      .catch(({ status }: TResponseQueryError) => {
        if (status === 409) {
          trackEvent('Email verification — all retries failed');
          dispatch(updateStatuses({ maxVerificationAttemptReached: true }));
        }
      });
  };

  return (
    <Box sx={styles.root}>
      <Button sx={styles.button} onClick={handleResend} disabled={disabled}>
        Resend code{delayPostfix}
      </Button>
    </Box>
  );
};

export default memo(ResendLink);
